import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, User, Package, Building2 } from "lucide-react";
import { ServiceJobDialog } from "@/components/services/ServiceJobDialog";
import { ServiceAttachments } from "@/components/services/ServiceAttachments";
import { SERVICE_STATUS, SERVICE_EDIT_ROLES, ServiceStatusBadge } from "@/components/services/serviceMeta";
import { fmtDate } from "@/components/equipment/equipmentMeta";

const chip = (on) => `text-xs font-bold tracking-tight px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-900"}`;

export default function ServiceJobs() {
  const { user } = useAuth();
  const canEdit = SERVICE_EDIT_ROLES.includes(user.role);
  const canDelete = ["admin", "superadmin", "hire_manager"].includes(user.role);
  const isTech = user.role === "service_technician" || (user.role === "rigger" && user.dual_role);
  const canSetStatus = canEdit || isTech;
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/service-jobs").then((r) => setJobs(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    if (canEdit) {
      api.get("/service-technicians").then((r) => setTechs(r.data)).catch(() => {});
      api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {});
    }
  }, [load, canEdit]);

  const setStatus = async (j, status) => {
    try {
      await api.patch(`/service-jobs/${j.id}/status`, { status });
      toast.success(`Marked ${SERVICE_STATUS[status].label}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (j) => {
    if (!window.confirm(`Delete service job '${j.title}'?`)) return;
    try {
      await api.delete(`/service-jobs/${j.id}`);
      toast.success("Service job deleted");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const shown = jobs.filter((j) => filter === "all" || (filter === "open" ? ["scheduled", "in_progress"].includes(j.status) : j.status === filter));

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="service-jobs-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display">{isTech ? "My Service Jobs" : "Service Jobs"}</h1>
        {canEdit && (
          <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-service-job-button"
            className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2">
            <Plus size={18} /> New Service Job
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="service-filter">
        {[["open", "Open"], ...Object.entries(SERVICE_STATUS).map(([k, s]) => [k, s.label]), ["all", "All"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`service-filter-${k}`} className={chip(filter === k)}>{l}</button>
        ))}
      </div>
      <div className="space-y-3" data-testid="service-jobs-list">
        {shown.map((j) => (
          <div key={j.id} data-testid={`service-job-${j.id}`} className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 flex flex-wrap gap-4 items-start">
            <div className="w-24 shrink-0 text-center border-r border-zinc-100 pr-4">
              <div className="text-xs font-bold uppercase tracking-wider text-brand">{fmtDate(j.scheduled_date).split(" ").slice(1).join(" ")}</div>
              <div className="font-heading text-3xl font-black text-zinc-900 leading-none">{fmtDate(j.scheduled_date).split(" ")[0]}</div>
              {j.scheduled_time && <div className="text-xs text-zinc-500 mt-1">{j.scheduled_time}</div>}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading text-lg font-bold tracking-tight text-zinc-900 break-words">{j.title}</span>
                <ServiceStatusBadge status={j.status} testid={`service-status-${j.id}`} />
              </div>
              {j.customer && <div className="text-sm text-zinc-700">{j.customer}</div>}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500 mt-1">
                {j.address && <span className="flex items-center gap-1"><MapPin size={12} /> {j.address}</span>}
                <span className="flex items-center gap-1" data-testid={`service-tech-${j.id}`}><User size={12} /> {j.assigned_name || "Unassigned"}</span>
                {j.equipment_name && <span className="flex items-center gap-1"><Package size={12} /> {j.equipment_name}</span>}
                {j.location && <span className="flex items-center gap-1" data-testid={`service-location-${j.id}`}><Building2 size={12} /> {j.location}</span>}
              </div>
              {j.description && <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap break-words">{j.description}</p>}
              <ServiceAttachments job={j} canEdit={canEdit} onChange={load} />
            </div>
            <div className="flex items-center gap-2">
              {canSetStatus && (
                <select value={j.status} onChange={(e) => setStatus(j, e.target.value)} data-testid={`service-status-change-${j.id}`}
                  className="px-2 py-1.5 border border-zinc-300 text-xs font-bold bg-white focus:outline-none focus:ring-2 focus:ring-brand">
                  {Object.entries(SERVICE_STATUS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
                </select>
              )}
              {canEdit && <button onClick={() => { setEditing(j); setOpen(true); }} data-testid={`edit-service-${j.id}`} className="text-zinc-400 hover:text-brand"><Pencil size={16} /></button>}
              {canDelete && <button onClick={() => remove(j)} data-testid={`delete-service-${j.id}`} className="text-zinc-400 hover:text-red-600"><Trash2 size={16} /></button>}
            </div>
          </div>
        ))}
        {shown.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg px-5 py-10 text-center text-zinc-400 text-sm">No service jobs here.</div>
        )}
      </div>
      {canEdit && <ServiceJobDialog open={open} onOpenChange={setOpen} editing={editing} technicians={techs} equipment={equipment} onSaved={load} />}
    </div>
  );
}
